import { isNullOrUndefined } from '@uzurv/core';
/* eslint-disable prefer-arrow/prefer-arrow-functions */
import { SafeUrl } from '@angular/platform-browser';
import { createReducer, on } from '@ngrx/store';
import {
  ChatMessage,
  CustomerServiceNote,
  CustomerServiceNoteDetail,
  DriverEta,
  DriverFareCalculation,
  FareReviewDetail,
  FareReviewStatus,
  IGeoCoords,
  IMapRoute,
  Itinerary,
  LyftDispatchRide,
  LyftDispatchRideFareAdjustmentReason,
  LyftDispatchRideReceipt,
  OrganizationRiderServiceLevel,
  OrganizationServiceLevel,
  PaymentAccount,
  PaymentMethod,
  ReservationDetail,
  RideRequestDetail,
  RiderFare,
  Trip,
  Vehicle
} from '@uzurv/clara';
import { cloneDeep } from 'lodash';
import { ReservationListItemVm } from '../../components/reservation';
import * as TripDetailActions from './trip-detail.actions';
import { AdjustedProviderCommissionDeviationState } from '../../services/provider-commission-adjustment-validation/provider-commission-adjustment-validation.service';
import { NonAcceptedRidesParams } from '../../pages/nonaccepted-rides/nonaccepted-rides.page';

export const tripDetailFeatureKey = 'tripDetail';
export interface TripDetailState {
  loading: boolean;
  editActionsDisabled: boolean;
  sendingChatMessage: boolean;
  trip: ReservationListItemVm;
  riderPaymentAccount: PaymentAccount;
  canEditBasicTrip: boolean;
  canEditWavTrip: boolean;
  uneditedTrip: ReservationListItemVm;
  editedPolylineId: number;
  driverProfilePictureUrl: SafeUrl;
  vehicle: Vehicle;
  eligibleVehicles: Array<Vehicle>;
  route: IMapRoute;
  completedRoute: IMapRoute;
  enRoute: IMapRoute;
  mapCenter: IGeoCoords;
  chatMessages: Array<ChatMessage>;
  editMode: boolean;
  notes: Array<CustomerServiceNoteDetail>;
  fareReviewStatus: FareReviewStatus;
  fareReviewDetail: FareReviewDetail;
  lyftDispatchRide: LyftDispatchRide;
  lyftDispatchRideFareAdjustmentReasons: Array<LyftDispatchRideFareAdjustmentReason>;
  lyftDispatchRideReceipt: LyftDispatchRideReceipt;
  fareCalculationNotes: Array<CustomerServiceNote>;
  estimateDriverFareCalculation: DriverFareCalculation;
  fareReviewNotes: Array<CustomerServiceNoteDetail>;
  itinerary: Itinerary;
  organizationServiceLevels: Array<OrganizationServiceLevel>;
  organizationRiderServiceLevels: Array<OrganizationRiderServiceLevel>;
  eta: DriverEta;
  paymentMethods: Array<PaymentMethod>;
  riderFares: Array<RiderFare>;
  isSubmittingRiderFares: boolean;
  nonacceptedRidesParams: NonAcceptedRidesParams;
  nonacceptedRidesList: {
    loading: boolean;
    nonacceptedRides: Array<Trip>;
  };
  adjustedProviderCommissionDeviationState: AdjustedProviderCommissionDeviationState;
  riderIdCardImage: SafeUrl;
  selectedTabIndex: number;
}

export const initialState: TripDetailState = {
  loading: false,
  editActionsDisabled: false,
  sendingChatMessage: false,
  trip: null,
  riderPaymentAccount: null,
  canEditBasicTrip: false,
  canEditWavTrip: false,
  uneditedTrip: null,
  editedPolylineId: null,
  driverProfilePictureUrl: null,
  vehicle: null,
  eligibleVehicles: [],
  route: null,
  completedRoute: null,
  enRoute: null,
  mapCenter: null,
  chatMessages: null,
  editMode: false,
  notes: null,
  fareReviewStatus: null,
  fareReviewDetail: null,
  lyftDispatchRide: null,
  lyftDispatchRideFareAdjustmentReasons: null,
  lyftDispatchRideReceipt: null,
  fareCalculationNotes: null,
  estimateDriverFareCalculation: null,
  fareReviewNotes: null,
  itinerary: null,
  organizationServiceLevels: null,
  organizationRiderServiceLevels: null,
  eta: null,
  paymentMethods: null,
  riderFares: null,
  isSubmittingRiderFares: false,
  nonacceptedRidesParams: {
    pageIndex: 0,
    pageSize: 0
  },
  nonacceptedRidesList: {
    loading: false,
    nonacceptedRides: []
  },
  adjustedProviderCommissionDeviationState: AdjustedProviderCommissionDeviationState.NONE,
  riderIdCardImage: null,
  selectedTabIndex: 0
};

function buildRoute(rideRequestDetail: RideRequestDetail): { route: IMapRoute; mapCenter: IGeoCoords } {
  return {
    route: {
      startNode: {
        lng: rideRequestDetail.pickupLocation.longitude,
        lat: rideRequestDetail.pickupLocation.latitude
      },
      endNode: {
        lng: rideRequestDetail.dropoffLocation.longitude,
        lat: rideRequestDetail.dropoffLocation.latitude
      },
      polyline: rideRequestDetail.polyline,
      distance: rideRequestDetail.distanceEstimate,
      duration: rideRequestDetail.durationEstimate
    },
    mapCenter: {
      lng: (rideRequestDetail.pickupLocation.longitude + rideRequestDetail.dropoffLocation.longitude) / 2,
      lat: (rideRequestDetail.pickupLocation.latitude + rideRequestDetail.dropoffLocation.latitude) / 2
    }
  };
}

export const reducer = createReducer(
  initialState,

  on(TripDetailActions.SetNonacceptedRidesPagination, (state, { pagination }) => {
    const updatedNonacceptedRidesParams = cloneDeep(state.nonacceptedRidesParams);
    const updatedNonacceptedRidesList = cloneDeep(state.nonacceptedRidesList);
    updatedNonacceptedRidesParams.pageIndex = pagination.pageIndex;
    updatedNonacceptedRidesParams.pageSize = pagination.pageSize;
    updatedNonacceptedRidesList.loading = true;

    return {
      ...state,
      nonacceptedRidesParams: updatedNonacceptedRidesParams,
      nonacceptedRidesList: updatedNonacceptedRidesList
    };
  }),

  on(TripDetailActions.FetchNonAcceptedRidesSuccess, (state, action) => {
    return {
      ...state,
      nonacceptedRidesList: { nonacceptedRides: action.nonacceptedRides, loading: false }
    };
  }),

  on(TripDetailActions.GetEligibleVehiclesForReservationSuccess, (state, action) => {
    return {
      ...state,
      eligibleVehicles: action.eligibleVehicles
    };
  }),

  on(TripDetailActions.ChangeDriverVehicleSuccess, (state, action) => {
    state.trip.vehicle = action.payload;
    state.trip.vehicleId = action.payload.id;

    return {
      ...state,
      loading: false,
      trip: state.trip,
      vehicle: action.payload
    };
  }),

  on(TripDetailActions.RestoreState, (state, action) => {
    return {
      ...action.state
    };
  }),

  on(TripDetailActions.GetTripDetail, () => {
    return {
      ...initialState,
      loading: true
    };
  }),

  on(TripDetailActions.GetTripDetailFail, state => {
    return {
      ...state,
      loading: false
    };
  }),

  on(TripDetailActions.GetTripDetailSuccess, (state, action) => {
    return {
      ...state,
      loading: false,
      trip:
        action.payload instanceof RideRequestDetail
          ? ReservationListItemVm.reservationListItemVmFromRideRequestDetail(action.payload)
          : ReservationListItemVm.reservationListItemVmFromReservationDetail(action.payload),
      chatMessages: action.payload instanceof ReservationDetail ? action.payload.chatMessages : null,
      ...(action.payload instanceof RideRequestDetail ? buildRoute(action.payload) : buildRoute(action.payload.rideRequestDetail))
    };
  }),

  on(TripDetailActions.SetPermissions, (state, action) => {
    return {
      ...state,
      canEditBasicTrip: action.payload.canEditBasicTrip,
      canEditWavTrip: action.payload.canEditWavTrip
    };
  }),

  on(TripDetailActions.GetRiderPaymentAccountSuccess, (state, action) => {
    state.trip.riderPaymentAccountString = action.payload.userDescription;
    return {
      ...state,
      trip: state.trip,
      riderPaymentAccount: action.payload
    };
  }),

  on(TripDetailActions.GetDriverProfilePictureSuccess, (state, action) => {
    return {
      ...state,
      driverProfilePictureUrl: action.safeUrl
    };
  }),

  on(TripDetailActions.GetRiderIgnoredDriversSuccess, (state, action) => {
    state.trip.rideRequestDetail.organizationRider.rider.ignoredDrivers = action.drivers;
    return state;
  }),

  on(TripDetailActions.GetCompletedRouteSuccess, (state, action) => {
    const completedRoute = {
      startNode: {
        lng: state.trip.rideRequestDetail.pickupLocation.longitude,
        lat: state.trip.rideRequestDetail.pickupLocation.latitude
      },
      endNode: {
        lng: state.trip.rideRequestDetail.dropoffLocation.longitude,
        lat: state.trip.rideRequestDetail.dropoffLocation.latitude
      },
      polyline: action.reservationRouteSummary.polyline
    };
    return {
      ...state,
      completedRoute: completedRoute
    };
  }),

  on(TripDetailActions.GetRouteSummaryForPeriod2Success, (state, action) => {
    return {
      ...state,
      enRoute: { polyline: action.reservationRouteSummary.polyline }
    };
  }),

  on(TripDetailActions.FavoriteDriverSuccess, state => {
    state.trip.rideRequestDetail.organizationRider.rider.favoriteDrivers.push(state.trip.driver);
    return {
      ...state,
      trip: state.trip
    };
  }),

  on(TripDetailActions.RemoveFavoritedDriverSuccess, state => {
    state.trip.rideRequestDetail.organizationRider.rider.favoriteDrivers.splice(
      state.trip.rideRequestDetail.organizationRider.rider.favoriteDrivers.findIndex(driver => driver.id === state.trip.driver.id)
    );
    return {
      ...state,
      trip: state.trip
    };
  }),

  on(TripDetailActions.IgnoreDriverSuccess, state => {
    state.trip.rideRequestDetail.organizationRider.rider.ignoredDrivers.push(state.trip.driver);
    return {
      ...state,
      trip: state.trip
    };
  }),

  on(TripDetailActions.RemoveIgnoredDriverSuccess, state => {
    state.trip.rideRequestDetail.organizationRider.rider.ignoredDrivers.splice(
      state.trip.rideRequestDetail.organizationRider.rider.ignoredDrivers.findIndex(driver => driver.id === state.trip.driver.id)
    );
    return {
      ...state,
      trip: state.trip
    };
  }),

  on(TripDetailActions.GetChatMessagesSuccess, (state, action) => {
    return {
      ...state,
      chatMessages: action.payload
    };
  }),

  on(TripDetailActions.ReadChatMessages, state => {
    if (!isNullOrUndefined(state.trip)) {
      state.trip.numberOfReadChatMessages = state.chatMessages ? state.chatMessages.length : 0;
    }
    return {
      ...state,
      trip: state.trip
    };
  }),

  on(TripDetailActions.SendChatMessage, state => {
    return {
      ...state,
      sendingChatMessage: true
    };
  }),

  on(TripDetailActions.SendChatMessageFail, TripDetailActions.SendChatMessageSuccess, state => {
    return {
      ...state,
      sendingChatMessage: false
    };
  }),

  on(TripDetailActions.GetNotesSuccess, (state, action) => {
    return {
      ...state,
      notes: action.customerServiceNoteDetails
    };
  }),

  on(TripDetailActions.EnterEditMode, state => {
    return state.editMode
      ? state
      : {
          ...state,
          editMode: true,
          uneditedTrip: cloneDeep(state.trip)
        };
  }),

  on(TripDetailActions.CancelEditMode, state => {
    return {
      ...state,
      editMode: false,
      trip: state.uneditedTrip
    };
  }),

  on(TripDetailActions.UpdateTripValue, (state, action) => {
    return {
      ...state,
      trip: action.payload.trip,
      editActionsDisabled: action.payload.getRideRequestInfo ? true : false
    };
  }),

  on(TripDetailActions.UpdateRideInfoSuccess, (state, action) => {
    Object.assign(state.trip.rideRequestDetail.transitProgramInfo, action.payload.transitProgramInfo);
    state.trip.rideRequestDetail.timeZone = action.payload.transitProgramInfo.timeZone;
    state.trip.rideRequestDetail.polyline = action.payload.rideRequestInfo.polyline;
    state.trip.rideRequestDetail.distanceEstimate = action.payload.rideRequestInfo.distanceEstimate;
    state.trip.rideRequestDetail.durationEstimate = action.payload.rideRequestInfo.durationEstimate;
    state.trip.rideRequestDetail.requestFee = action.payload.rideRequestInfo.riderFee;

    if (state.trip.rideRequestDetail.dropoffDateProvided) {
      state.trip.rideRequestDetail.pickupDate = action.payload.rideRequestInfo.estimatedPickupDate;
    } else {
      state.trip.rideRequestDetail.dropoffDate = action.payload.rideRequestInfo.estimatedDropoffDate;
    }

    return {
      ...state,
      editActionsDisabled: false,
      trip: state.trip,
      editedPolylineId: action.payload.rideRequestInfo.polylineId,
      ...buildRoute(state.trip.rideRequestDetail)
    };
  }),

  on(TripDetailActions.UpdateReservationStatusSuccess, (state, action) => {
    return {
      ...state,
      trip: action.reservationListItemVm
    };
  }),

  on(TripDetailActions.UpdateRideInfoFail, state => {
    return {
      ...state,
      editActionsDisabled: false
    };
  }),

  on(TripDetailActions.PatchIncentive, TripDetailActions.PatchRideRequest, state => {
    return {
      ...state,
      editActionsDisabled: true
    };
  }),

  on(TripDetailActions.PatchIncentiveFail, TripDetailActions.PatchRideRequestFail, state => {
    return {
      ...state,
      editMode: true,
      editActionsDisabled: false
    };
  }),

  on(TripDetailActions.PatchIncentiveSuccess, TripDetailActions.PatchRideRequestSuccess, state => {
    return {
      ...state,
      editMode: false,
      editActionsDisabled: false
    };
  }),

  on(TripDetailActions.DispatchToLyftSuccess, state => {
    state.trip.rideRequestDetail.dispatchedToLyft = true;
    return state;
  }),

  on(TripDetailActions.GetFareReviewStatusSuccess, (state, action) => {
    return {
      ...state,
      fareReviewStatus: action.fareReviewStatus
    };
  }),

  on(TripDetailActions.GetFareReviewDetailSuccess, (state, action) => {
    action.fareReviewDetail.driverFareCalculations.sort((a, b) => (a.createdDate > b.createdDate ? -1 : 1));
    return {
      ...state,
      fareReviewDetail: action.fareReviewDetail,
      enRoute: { polyline: action.fareReviewDetail.period2Polyline }
    };
  }),

  on(TripDetailActions.FindActiveByReservationIdSuccess, (state, action) => {
    return {
      ...state,
      lyftDispatchRide: action.lyftDispatchRide
    };
  }),

  on(TripDetailActions.GetLyftDispatchRideFareAdjustmentReasonsSuccess, (state, action) => {
    const reason = new LyftDispatchRideFareAdjustmentReason();
    reason.id = -1;
    reason.code = '-1';
    reason.name = 'Approve As Is';
    action.lyftDispatchRideFareAdjustmentReasons.unshift(reason);

    return {
      ...state,
      lyftDispatchRideFareAdjustmentReasons: action.lyftDispatchRideFareAdjustmentReasons
    };
  }),

  on(TripDetailActions.GetLyftDispatchRideReceiptSuccess, (state, action) => {
    return {
      ...state,
      lyftDispatchRideReceipt: action.lyftDispatchRideReceipt
    };
  }),

  on(TripDetailActions.GetFareCalculationNotesSuccess, (state, action) => {
    return {
      ...state,
      fareCalculationNotes: action.fareCalculationNotes
    };
  }),

  on(TripDetailActions.GetFareAdjustmentEstimateSuccess, (state, action) => {
    return {
      ...state,
      estimateDriverFareCalculation: action.estimateDriverFareCalculation
    };
  }),

  on(TripDetailActions.SetAdjustedProviderCommissionDeviationState, (state, action) => {
    return {
      ...state,
      adjustedProviderCommissionDeviationState: action.adjustedProviderCommissionDeviationState
    };
  }),

  on(TripDetailActions.CancelFareAdjustment, TripDetailActions.SubmitFareAdjustmentSuccess, (state, action) => {
    return {
      ...state,
      adjustedProviderCommissionDeviationState: AdjustedProviderCommissionDeviationState.NONE
    };
  }),

  on(TripDetailActions.GetFareReviewNotesSuccess, (state, action) => {
    return {
      ...state,
      fareReviewNotes: action.fareReviewNotes
    };
  }),

  on(TripDetailActions.GetItinerarySuccess, (state, action) => {
    const sortedItinerary = action.itinerary;
    sortedItinerary.rideRequests.sort((a, b) => (a.id > b.id ? 1 : -1));
    return {
      ...state,
      itinerary: action.itinerary
    };
  }),

  on(TripDetailActions.GetOrganizationServiceLevelsSuccess, (state, action) => {
    return {
      ...state,
      organizationServiceLevels: action.organizationServiceLevels
    };
  }),

  on(TripDetailActions.GetOrganizationRiderServiceLevelsSuccess, (state, action) => {
    return {
      ...state,
      organizationRiderServiceLevels: action.organizationServiceLevels
    };
  }),

  on(TripDetailActions.EtaReceived, (state, action) => {
    return {
      ...state,
      eta: action.payload
    };
  }),

  on(TripDetailActions.GetPaymentMethodsSuccess, (state, action) => {
    return {
      ...state,
      paymentMethods: action.paymentMethods
    };
  }),

  on(TripDetailActions.GetRiderFaresSuccess, (state, action) => {
    return {
      ...state,
      riderFares: action.rideFares
    };
  }),

  on(TripDetailActions.SubmitRiderFare, state => {
    return {
      ...state,
      isSubmittingRiderFares: true
    };
  }),

  on(TripDetailActions.SubmitRiderFareSuccess, (state, action) => {
    return {
      ...state,
      riderFares: action.payload.riderFares,
      isSubmittingRiderFares: false
    };
  }),

  on(TripDetailActions.SubmitRiderFareFail, state => {
    return {
      ...state,
      isSubmittingRiderFares: false
    };
  }),

  on(TripDetailActions.OpenRecoveryRideTab, (state, action) => {
    const updateTrip: ReservationListItemVm = cloneDeep(state.trip);
    updateTrip.cancelled = true;
    updateTrip.rideRequestDetail.childRideRequestId = action.recoveryItinerary.rideRequests[0].id;
    return {
      ...state,
      trip: updateTrip
    };
  }),

  on(TripDetailActions.SetNonAcceptedRidesParams, (state, { state: partialState }) => ({
    ...state,
    ...partialState
  })),

  on(TripDetailActions.GetRiderIdCardImageSuccess, (state, action) => {
    return {
      ...state,
      riderIdCardImage: action.safeUrl
    };
  }),

  on(TripDetailActions.SetSelectedTabIndex, (state, action) => {
    return {
      ...state,
      selectedTabIndex: action.index
    };
  })
);
